import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Container, Row, Col, ProgressBar, Badge, Spinner, Alert } from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import { getTrainingDashboard } from "../../../services/TrainingService";
import TrainingAccessControl from "./TrainingAccessControl";
import "./Training.css";

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      try {
        // Tentar carregar o dashboard completo
        const data = await getTrainingDashboard();
        console.log("Dashboard data received:", data); // Debug log
        setDashboardData(data);
      } catch (dashboardError) {
        console.warn("Dashboard endpoint failed, falling back to categories:", dashboardError);
        
        // Fallback: carregar apenas as categorias
        const { getTrainingCategories } = await import("../../../services/TrainingService");
        const categories = await getTrainingCategories();
        
        setDashboardData({
          categories: categories || [],
          next_videos: [],
          recent_activity: []
        });
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setError("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/training/category/${categoryId}`);
  };

  const handleVideoClick = (videoId) => {
    navigate(`/training/video/${videoId}`);
  };

  const getStatusBadge = (category) => {
    if (category.is_completed) {
      return <Badge bg="success">Concluído</Badge>;
    } else if (!category.is_unlocked) {
      return <Badge bg="secondary">Bloqueado</Badge>;
    } else if (category.completion_percentage > 0) {
      return <Badge bg="warning">Em Progresso</Badge>;
    } else {
      return <Badge bg="info">Disponível</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando dashboard...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <TrainingAccessControl>
      <Container className="mt-4">
        <div className="page-header mb-4">
          <h1>Treinamentos</h1>
          <p className="text-muted">
            Acompanhe seu progresso nos treinamentos disponíveis
          </p>
        </div>

      {/* Categorias de Treinamento */}
      <Row>
        <Col xs={12}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Categorias de Treinamento</h5>
            </Card.Header>
            <Card.Body>
              {Array.isArray(dashboardData?.categories) && dashboardData.categories.length > 0 ? (
                <Row>
                  {dashboardData.categories.map((category) => (
                    <Col key={category.id} xs={12} md={6} lg={4} className="mb-3">
                      <Card 
                        className={`h-100 training-category-card ${!category.is_unlocked ? 'category-locked' : ''}`}
                        style={{ cursor: category.is_unlocked ? 'pointer' : 'not-allowed' }}
                        onClick={() => category.is_unlocked && handleCategoryClick(category.id)}
                      >
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title">{category.name}</h6>
                            {getStatusBadge(category)}
                          </div>
                          
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">
                                {category.completed_count} de {category.videos_count} vídeos
                              </small>
                              <small className="text-muted">
                                {Math.round(category.completion_percentage)}%
                              </small>
                            </div>
                            <ProgressBar 
                              now={category.completion_percentage} 
                              variant={category.is_completed ? "success" : "primary"}
                              style={{ height: "8px" }}
                            />
                          </div>

                          {!category.is_unlocked && (
                            <div className="text-center text-muted">
                              <i className="las la-lock" style={{ fontSize: "24px" }}></i>
                              <p className="small mb-0">Categoria bloqueada</p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="las la-video" style={{ fontSize: "48px" }}></i>
                  <p>Nenhuma categoria de treinamento disponível</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Próximos Vídeos */}
        <Col xs={12} lg={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Próximos Vídeos</h5>
            </Card.Header>
            <Card.Body>
              {Array.isArray(dashboardData?.next_videos) && dashboardData.next_videos.length > 0 ? (
                <div className="video-list">
                  {dashboardData.next_videos.map((video) => (
                    <div 
                      key={video.id} 
                      className="video-item d-flex align-items-center p-3 border rounded mb-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleVideoClick(video.id)}
                    >
                      <div className="video-thumbnail me-3">
                        {video.thumbnail_url ? (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title}
                            className="rounded"
                            style={{ width: "80px", height: "60px", objectFit: "cover" }}
                          />
                        ) : (
                          <div 
                            className="bg-light d-flex align-items-center justify-content-center rounded"
                            style={{ width: "80px", height: "60px" }}
                          >
                            <i className="las la-play-circle" style={{ fontSize: "50px" }}></i>
                          </div>
                        )}
                      </div>
                      <div className="video-info flex-grow-1">
                        <h6 className="mb-1">{video.title}</h6>
                        <p className="text-muted small mb-0">
                          {video.category_name} • {video.duration_display}
                        </p>
                      </div>
                      <div className="video-action">
                        <i className="las la-chevron-right text-muted"></i>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="las la-check-circle" style={{ fontSize: "48px" }}></i>
                  <p>Todos os vídeos disponíveis foram assistidos!</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Atividade Recente */}
        <Col xs={12} lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Atividade Recente</h5>
            </Card.Header>
            <Card.Body>
              {Array.isArray(dashboardData?.recent_activity) && dashboardData.recent_activity.length > 0 ? (
                <div className="activity-list">
                  {dashboardData.recent_activity.map((activity, index) => (
                    <div key={index} className="activity-item d-flex align-items-start mb-3">
                      <div className="activity-icon me-2">
                        <i className={`las ${activity.completed ? 'la-check-circle text-success' : 'la-play-circle'}`}></i>
                      </div>
                      <div className="activity-content">
                        <p className="mb-1 small">
                          <strong>{activity.video_title}</strong>
                        </p>
                        <p className="text-muted small mb-1">
                          {activity.category_name}
                        </p>
                        <p className="text-muted small mb-0">
                          {formatDate(activity.last_update)}
                        </p>
                        {!activity.completed && (
                          <div className="mt-1">
                            <ProgressBar 
                              now={activity.progress_percent} 
                              size="sm"
                              style={{ height: "4px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="las la-history" style={{ fontSize: "32px" }}></i>
                  <p className="small mb-0">Nenhuma atividade recente</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
    </TrainingAccessControl>
  );
};

export default TrainingDashboard;